import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const HealthTracking = ({ trackingData, onAddEntry }) => {
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState('');
  const [newEntry, setNewEntry] = useState({
    value: '',
    notes: ''
  });

  const metrics = [
    { id: 'blood-pressure', label: 'Blood Pressure', icon: 'Activity', unit: 'mmHg' },
    { id: 'blood-glucose', label: 'Blood Glucose', icon: 'Droplet', unit: 'mg/dL' },
    { id: 'weight', label: 'Weight', icon: 'Scale', unit: 'lbs' },
    { id: 'temperature', label: 'Temperature', icon: 'Thermometer', unit: '°F' },
    { id: 'heart-rate', label: 'Heart Rate', icon: 'Heart', unit: 'bpm' },
    { id: 'oxygen', label: 'Oxygen Saturation', icon: 'Wind', unit: '%' }
  ];

  const handleAddEntry = () => {
    if (selectedMetric && newEntry?.value) {
      onAddEntry({
        metric: selectedMetric,
        value: newEntry?.value,
        notes: newEntry?.notes,
        date: new Date()?.toISOString()
      });
      setNewEntry({ value: '', notes: '' });
      setSelectedMetric('');
      setShowAddEntry(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-secondary/10 rounded-lg">
            <Icon name="Activity" size={20} color="var(--color-secondary)" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground">Health Tracking</h2>
            <p className="text-xs md:text-sm text-muted-foreground">Log your vital signs and symptoms</p>
          </div>
        </div>
        <Button
          variant="default"
          size="sm"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setShowAddEntry(!showAddEntry)}
        >
          <span className="hidden md:inline">Add Entry</span>
          <span className="md:hidden">Add</span>
        </Button>
      </div>
      {showAddEntry && (
        <div className="bg-background rounded-lg border border-border p-4 mb-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Select Metric</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {metrics?.map((metric) => (
                <button
                  key={metric?.id}
                  onClick={() => setSelectedMetric(metric?.id)}
                  className={`flex items-center gap-2 p-3 rounded-lg border transition-smooth ${
                    selectedMetric === metric?.id
                      ? 'bg-primary/10 border-primary text-primary' :'bg-background border-border text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={metric?.icon} size={18} />
                  <span className="text-xs md:text-sm font-medium">{metric?.label}</span>
                </button>
              ))}
            </div>
          </div>
          {selectedMetric && (
            <>
              <Input
                label="Value"
                type="text"
                placeholder={`Enter ${metrics?.find(m => m?.id === selectedMetric)?.unit}`}
                value={newEntry?.value}
                onChange={(e) => setNewEntry({ ...newEntry, value: e?.target?.value })}
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Notes (Optional)</label>
                <textarea
                  className="w-full min-h-[80px] px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  placeholder="Add any additional notes..."
                  value={newEntry?.notes}
                  onChange={(e) => setNewEntry({ ...newEntry, notes: e?.target?.value })}
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="default"
                  size="sm"
                  iconName="Save"
                  iconPosition="left"
                  onClick={handleAddEntry}
                >
                  Save Entry
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowAddEntry(false);
                    setSelectedMetric('');
                    setNewEntry({ value: '', notes: '' });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {metrics?.map((metric) => {
          const data = trackingData?.filter(entry => entry?.metric === metric?.id);
          const latestEntry = data?.[0];

          return (
            <div
              key={metric?.id}
              className="bg-background rounded-lg border border-border p-4"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                  <Icon name={metric?.icon} size={20} color="var(--color-primary)" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-foreground">{metric?.label}</h3>
                  <p className="text-xs text-muted-foreground">{data?.length} entries</p>
                </div>
              </div>
              {latestEntry ? (
                <>
                  <p className="text-2xl font-bold text-foreground mb-1">
                    {latestEntry?.value} <span className="text-sm font-normal text-muted-foreground">{metric?.unit}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">{formatDate(latestEntry?.date)}</p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">No entries yet</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HealthTracking;